// ═══════════════════════════════════════════════════════════════════
// requestCache — Caché en memoria con deduplicación de peticiones en vuelo (TASK-122)
//
// Problema que resuelve: el panel de administración pedía varias veces por carga
// de página los mismos recursos (/api/company, /api/branches, /api/employee,
// /api/services). StrictMode monta los efectos dos veces y varios hooks/contextos
// piden lo mismo en paralelo, lo que terminaba disparando el rate limiter (429).
//
// Reglas:
// - Si ya hay una petición en vuelo para la misma clave, se reutiliza su promesa.
// - Una respuesta exitosa se guarda durante `ttl` ms; los errores NO se cachean.
// - Cada consumidor puede pasar su propio AbortSignal: al abortar solo deja de
//   esperar su promesa, sin cancelar la petición compartida con los demás.
// - Cualquier escritura (POST/PUT/PATCH/DELETE) vía fetchWithAuth limpia la caché.
// - Este módulo no importa nada para evitar dependencias circulares con fetchWithAuth.
// ═══════════════════════════════════════════════════════════════════

// Tiempo por defecto que una respuesta exitosa se considera fresca
export const DEFAULT_TTL_MS = 10000;

// Respuestas resueltas: clave → { data, expiresAt }
const resolvedEntries = new Map();
// Peticiones en vuelo: clave → Promise<data>
const inflightRequests = new Map();
// Generación de la caché: se incrementa en cada limpieza para descartar respuestas viejas
let cacheGeneration = 0;

// Crear un error de cancelación con el mismo nombre que usa fetch al abortar
const createAbortError = () => {
    const error = new Error('La petición fue cancelada');
    error.name = 'AbortError';
    return error;
};

// Entregar una copia independiente para que un consumidor no mute los datos de otro
const cloneData = (data) => {
    if (data === undefined || data === null || typeof data !== 'object') return data;
    try {
        return JSON.parse(JSON.stringify(data));
    } catch {
        // Si no es serializable, devolver la referencia original
        return data;
    }
};

// Envolver la promesa compartida para que respete el AbortSignal del consumidor
const withConsumerSignal = (sharedPromise, signal) => {
    // Sin signal: solo clonar el resultado
    if (!signal) return sharedPromise.then(cloneData);
    // Signal ya abortado: rechazar de inmediato sin esperar
    if (signal.aborted) return Promise.reject(createAbortError());

    return new Promise((resolve, reject) => {
        // Al abortar, este consumidor deja de esperar; la petición sigue para los demás
        const onAbort = () => reject(createAbortError());
        signal.addEventListener('abort', onAbort, { once: true });

        sharedPromise.then(
            (data) => {
                signal.removeEventListener('abort', onAbort);
                resolve(cloneData(data));
            },
            (error) => {
                signal.removeEventListener('abort', onAbort);
                reject(error);
            }
        );
    });
};

/**
 * Ejecutar `fetcher` una sola vez por clave mientras esté en vuelo o fresco en caché.
 * @param {string} key - Clave única del recurso (normalmente la URL).
 * @param {() => Promise<any>} fetcher - Función que hace la petición real. Debe lanzar si falla.
 * @param {{ signal?: AbortSignal, ttl?: number, force?: boolean }} [options]
 *   force: ignora la respuesta cacheada (pero reutiliza una petición ya en vuelo).
 * @returns {Promise<any>} Copia de los datos obtenidos.
 */
export const cachedRequest = (key, fetcher, { signal, ttl = DEFAULT_TTL_MS, force = false } = {}) => {
    // 0. El consumidor ya se desmontó: no disparar ninguna petición
    if (signal?.aborted) return Promise.reject(createAbortError());

    // 1. Respuesta fresca en caché
    const entry = resolvedEntries.get(key);
    if (!force && entry && entry.expiresAt > Date.now()) {
        return withConsumerSignal(Promise.resolve(entry.data), signal);
    }

    // 2. Petición idéntica ya en vuelo: compartirla
    const inflight = inflightRequests.get(key);
    if (inflight) {
        return withConsumerSignal(inflight, signal);
    }

    // 3. Nueva petición compartida
    const generation = cacheGeneration;
    const sharedPromise = (async () => {
        try {
            const data = await fetcher();
            // Solo cachear si nadie limpió la caché mientras la petición estaba en vuelo
            if (generation === cacheGeneration && ttl > 0) {
                resolvedEntries.set(key, { data, expiresAt: Date.now() + ttl });
            }
            return data;
        } finally {
            // Liberar la clave en vuelo (solo si sigue siendo esta misma promesa)
            if (inflightRequests.get(key) === sharedPromise) {
                inflightRequests.delete(key);
            }
        }
    })();

    inflightRequests.set(key, sharedPromise);
    return withConsumerSignal(sharedPromise, signal);
};

/**
 * Limpiar toda la caché (tras una escritura, al iniciar/cerrar sesión o entre tests).
 * Las peticiones en vuelo se sueltan para que el próximo consumidor pida datos nuevos.
 */
export const clearRequestCache = () => {
    // Invalidar las respuestas que todavía estén en vuelo para que no se cacheen
    cacheGeneration += 1;
    resolvedEntries.clear();
    inflightRequests.clear();
};
